import { useAnalytics } from '@/hooks/analytics';
import { CookiePreferenceCategory, CookiePreferences } from '@tikkie/blokkie';
import { useCookies as useReactCookies } from 'react-cookie';
import { CookieSetOptions } from 'universal-cookie';

export const FUNCTIONAL_PREFERENCE = {
  type: CookiePreferenceCategory.FUNCTIONAL,
  enabled: true,
  readOnly: true,
};

export const ANALYTICAL_PREFERENCE = {
  type: CookiePreferenceCategory.ANALYTICAL,
  enabled: true,
  readOnly: true,
};

export const PERSONAL_PREFERENCE = {
  type: CookiePreferenceCategory.PERSONAL,
  enabled: true,
};

const IS_DEV_ENV = import.meta.env.DEV;

export function useTikkieCookies() {
  const [cookies, setCookie] = useReactCookies();
  const { updateConsent } = useAnalytics();

  function addYears(date: Date, years: number): Date {
    const newDate = new Date(date);
    newDate.setFullYear(newDate.getFullYear() + years);
    return newDate;
  }

  function createExpiresDate(): Date {
    return addYears(new Date(), 1);
  }

  const handleCookies = (preferences: CookiePreferences) => {
    const expires = createExpiresDate();
    const privateCookie = preferences.find(
      (preference) => preference.type === CookiePreferenceCategory.PERSONAL
    );
    const enablePersonalCookies = privateCookie?.enabled ?? false;
    const cookieBody: CookieSetOptions = {
      path: '/',
      expires,
    };
    if (!IS_DEV_ENV) {
      cookieBody.domain = '.tikkie.me';
    }
    setCookie('cookieTarget', enablePersonalCookies, cookieBody);
    setCookie('cookieSocial', enablePersonalCookies, cookieBody);
    // Set analytics consent mode
    updateConsent(preferences);
  };

  const isCookiesSet = (): boolean =>
    cookies !== null &&
    cookies.cookieTarget !== undefined &&
    cookies.cookieSocial !== undefined;

  const cookiePreferences: CookiePreferences = [
    FUNCTIONAL_PREFERENCE,
    ANALYTICAL_PREFERENCE,
    PERSONAL_PREFERENCE,
  ];

  const currentCookiePreferences = () => [
    FUNCTIONAL_PREFERENCE,
    ANALYTICAL_PREFERENCE,
    {
      ...PERSONAL_PREFERENCE,
      enabled: cookies.cookieSocial === true && cookies.cookieTarget === true,
    },
  ];

  return {
    cookiePreferences,
    currentCookiePreferences,
    handleCookies,
    isCookiesSet,
  };
}
